import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  AutoAwesome as AutoIcon,
  StickyNote2 as NoteIcon,
  StickyNote2Outlined as NoteOutlinedIcon,
} from '@mui/icons-material';
import { useSaveTranslation, useAutoTranslate } from '../../hooks/useTranslations';
import type { Segment } from '../../types/api.types';
import toast from 'react-hot-toast';
import { showCelebrationToast } from '../../utils/celebrations';
import { decodeHtmlEntities } from '../../utils/textUtils';
import { useAutoSave } from '../../hooks/useAutoSave';
import { SaveStatus } from '../common/SaveStatus';
import { NoteModal } from '../notes/NoteModal';
import { useTranslation } from 'react-i18next';

interface TranslationEditorProps {
  segment: Segment;
  projectId: number;
  episodeId: number;
}

export const TranslationEditor = ({ segment, projectId, episodeId }: TranslationEditorProps) => {
  const { t: tSegments } = useTranslation('segments');
  // Get the most recent manual translation (highest ID = most recent)
  const getLatestManualTranslation = () => {
    const manualTranslations = segment.translations?.filter(t => t.source === 'manual') || [];
    if (manualTranslations.length === 0) return '';
    const latest = manualTranslations.reduce((prev, current) => 
      (current.id > prev.id) ? current : prev
    );
    return latest.translation || '';
  };
  
  const [manualText, setManualText] = useState(getLatestManualTranslation());
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);

  const { mutate: saveTranslation } = useSaveTranslation();
  const { mutate: autoTranslate, isPending: isTranslating } = useAutoTranslate();

  const autoTranslation = segment.translations?.find(t => t.source === 'Google' || t.source === 'Google:Manual');

  // Update manual text when segment changes or when translations change
  // Load manual text only when segment changes (not when content changes)
  // This prevents refetched data from overwriting unsaved user edits
  useEffect(() => {
    const manualTranslation = getLatestManualTranslation();
    setManualText(manualTranslation);
  }, [segment.id]);

  const handleSave = () => {
    saveTranslation({
      segmentId: segment.id,
      name: `Segment ${segment.id}`,
      translation: manualText,
      source: 'manual',
    }, {
      onSuccess: () => {
        showCelebrationToast('TRANSLATION_SAVED', 'Translation added!', '🌍');
      },
    });
    // Keep isDirty as true to prevent refetched data from overwriting our save
    // It will reset when user navigates to a new segment
  };

  // Auto-save functionality
  const { status: saveStatus, lastSavedAt, triggerSave } = useAutoSave({
    onSave: handleSave,
    value: manualText,
    initialValue: getLatestManualTranslation(),
    delay: 5000,
    enabled: true,
  });

  const handleAutoTranslate = () => {
    autoTranslate({
      id: segment.id,
      projectId,
      episodeId,
      startTime: segment.startTime,
      endTime: segment.endTime,
    });
  };

  const handleCopyAuto = () => {
    if (autoTranslation?.translation) {
      navigator.clipboard.writeText(decodeHtmlEntities(autoTranslation.translation));
      toast.success('Copied to clipboard');
    }
  };

  return (
    <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.default' }} variant="outlined">
      <Grid container spacing={2} sx={{ height: '100%', width: '100%' }}>
        {/* Manual Translation */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0, '@media (min-width: 900px)': { flex: '1 1 0' } }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Your Translation</Typography>
              <Tooltip title={segment.hasTranslationNote ? tSegments('noteViewEditTooltip') : tSegments('noteAddTooltip')}>
                <IconButton
                  size="small"
                  onClick={() => setIsNoteModalOpen(true)}
                  color={segment.hasTranslationNote ? 'primary' : 'default'}
                  data-testid="translation-note-icon"
                >
                  {segment.hasTranslationNote ? <NoteIcon fontSize="small" /> : <NoteOutlinedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
            <TextField
              multiline
              minRows={4}
              fullWidth
              placeholder="Type your translation..."
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              // Allow typing while saving; do not disable input
              sx={{
                mb: 1,
                bgcolor: 'background.paper',
                '& .MuiInputBase-root': {
                  minHeight: '150px',
                  maxHeight: '300px',
                  overflow: 'auto',
                  alignItems: 'flex-start',
                },
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                onClick={triggerSave}
                disabled={
                  !manualText.trim() ||
                  manualText === getLatestManualTranslation()
                }
                size="small"
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save'}
              </Button>
              <SaveStatus status={saveStatus} lastSavedAt={lastSavedAt} />
            </Stack>
          </Box>
        </Grid>

        {/* Auto Translation */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0, '@media (min-width: 900px)': { flex: '1 1 0' } }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Auto Translation
              </Typography>
              {autoTranslation && (
                <IconButton size="small" onClick={handleCopyAuto} title="Copy to clipboard">
                  <CopyIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>


            {!autoTranslation && (
              <Button
                variant="outlined"
                startIcon={isTranslating ? <CircularProgress size={16} /> : <AutoIcon />}
                onClick={handleAutoTranslate}
                disabled={isTranslating}
                size="small"
              >
                {isTranslating ? 'Translating...' : 'Generate Auto-Translation'}
              </Button>
            )}

            {autoTranslation && (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  minHeight: '150px',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
                variant="outlined"
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {autoTranslation.translation ? decodeHtmlEntities(autoTranslation.translation) : 'No translation available'}
                </Typography>
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>
      <NoteModal
        open={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        segmentId={segment.id}
        noteType="translation"
        sourceText={manualText}
        hasExistingNote={segment.hasTranslationNote}
      />
    </Paper>
  );
};
